import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { useCoffee } from "@/context/CoffeeContext";
import { BTN_PRIMARY, BTN_GHOST, ICON_BTN, INPUT } from "@/lib/styles";
import { HelpCircle, X, Play, Pencil, Loader2, ListChecks } from "lucide-react";
import { toast } from "sonner";

function ytEmbed(url) {
  if (!url) return null;
  const m = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([\w-]{11})/);
  return m ? `https://www.youtube.com/embed/${m[1]}` : null;
}

export function TutorialModal({ tool, open, onClose }) {
  const { status } = useCoffee();
  const isAdmin = status?.is_admin;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/tutorials/${tool}`);
      setData(data);
    } catch {
      setData(null);
    } finally { setLoading(false); }
  }, [tool]);

  useEffect(() => { if (open) { setEditing(false); load(); } }, [open, load]);

  if (!open) return null;

  const startEdit = () => {
    setForm({
      title: data?.title || "",
      description: data?.description || "",
      steps: (data?.steps || []).join("\n"),
      video_type: data?.video_type || "",
      video_url: data?.video_url || "",
    });
    setEditing(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        title: form.title,
        description: form.description,
        steps: form.steps.split("\n").map((s) => s.trim()).filter(Boolean),
        video_type: form.video_url ? (ytEmbed(form.video_url) ? "youtube" : "url") : "",
        video_url: form.video_url,
      };
      const { data: saved } = await api.put(`/tutorials/${tool}`, payload);
      setData(saved);
      setEditing(false);
      toast.success("Tutorial actualizado");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const embed = ytEmbed(data?.video_url);

  return (
    <div className="fixed inset-0 z-[80] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => !saving && onClose?.()} data-testid="tutorial-modal">
      <div className="w-full max-w-lg bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 shadow-2xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-heading font-medium text-lg flex items-center gap-2">
            <HelpCircle size={20} className="text-indigo-500" /> {data?.title || "Tutorial"}
          </h3>
          <div className="flex items-center gap-1">
            {isAdmin && !editing && <button onClick={startEdit} className={ICON_BTN} title="Editar tutorial" data-testid="tutorial-edit"><Pencil size={15} /></button>}
            <button onClick={() => onClose?.()} className={ICON_BTN} data-testid="tutorial-close"><X size={16} /></button>
          </div>
        </div>

        {loading ? (
          <div className="py-16 flex justify-center text-slate-400"><Loader2 className="animate-spin" size={24} /></div>
        ) : editing ? (
          <div className="space-y-3 mt-3">
            <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Título" className={INPUT} data-testid="tutorial-title-input" />
            <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Descripción breve" className={INPUT} />
            <div>
              <span className="font-mono2 text-[11px] text-slate-400 block mb-1">Pasos (uno por línea)</span>
              <textarea value={form.steps} onChange={(e) => setForm({ ...form, steps: e.target.value })} rows={5} className={`${INPUT} resize-none`} data-testid="tutorial-steps-input" />
            </div>
            <div>
              <span className="font-mono2 text-[11px] text-slate-400 block mb-1">Enlace del video (YouTube o MP4)</span>
              <input value={form.video_url} onChange={(e) => setForm({ ...form, video_url: e.target.value })} placeholder="https://youtu.be/... o https://.../video.mp4" className={INPUT} data-testid="tutorial-video-input" />
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <button onClick={() => setEditing(false)} className={BTN_GHOST} disabled={saving}>Cancelar</button>
              <button onClick={save} className={BTN_PRIMARY} disabled={saving} data-testid="tutorial-save">{saving ? "Guardando..." : "Guardar"}</button>
            </div>
          </div>
        ) : (
          <>
            {data?.description && <p className="font-body text-sm text-slate-500 dark:text-slate-400 mb-4">{data.description}</p>}
            <div className="rounded-xl overflow-hidden bg-slate-100 dark:bg-slate-950 mb-4 aspect-video flex items-center justify-center" data-testid="tutorial-video">
              {embed ? (
                <iframe src={embed} title="tutorial" className="w-full h-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
              ) : data?.video_url ? (
                <video src={data.video_url} controls className="w-full h-full" />
              ) : (
                <div className="text-center text-slate-400 px-6">
                  <Play size={30} className="mx-auto mb-2 opacity-60" />
                  <p className="font-body text-xs">Video próximamente. Mientras tanto, sigue los pasos 👇</p>
                </div>
              )}
            </div>
            {data?.steps?.length > 0 && (
              <ol className="space-y-2.5" data-testid="tutorial-steps">
                {data.steps.map((s, i) => (
                  <li key={i} className="flex gap-3 text-sm">
                    <span className="shrink-0 w-6 h-6 rounded-full bg-indigo-500/10 text-indigo-500 flex items-center justify-center font-mono2 text-xs font-semibold">{i + 1}</span>
                    <span className="text-slate-700 dark:text-slate-300 pt-0.5">{s}</span>
                  </li>
                ))}
              </ol>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export function HelpButton({ tool, className = "" }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-slate-400 hover:text-indigo-500 hover:bg-indigo-50 dark:hover:bg-indigo-500/10 transition-colors ${className}`}
        title="¿Cómo funciona?"
        data-testid={`help-${tool}`}
      >
        <HelpCircle size={18} />
      </button>
      <TutorialModal tool={tool} open={open} onClose={() => setOpen(false)} />
    </>
  );
}
